import React, { useContext, useEffect, useState } from 'react'
import { Icon, ServiceSection, SectionTitle, StepNavigationButtons } from '@pinflag/pinflag-ui-kit'
import { DataContext } from '../../context/DataContext'
import { motion } from 'framer-motion'
import { findClosestPoint, findPointByDistrictOrRegion } from '../../helpers/findClosestPoint'
import { setServiceAndShippingcost } from '../../helpers/utils'
import { numberParser } from '../../helpers/numberParser'

function ShippingMethodStep () {
  const {
    steps,
    setSteps,
    setSelectedShippingMethod,
    setSelectedServiceType,
    setCartData,
    deliveryInfo,
    pointsData,
    setPointsData,
    ecommerceInfo,
    companyInfo,
    addressInfo,
    setAddressInfo
  } = useContext(DataContext)

  const { localPoints, pinflagPoints } = pointsData
  const { primaryColor, secondaryColor } = companyInfo
  const [selectedOption, setSelectedOption] = useState(null)
  const [closestLocal, setClosestLocal] = useState(null)
  const [closestPinflag, setClosestPinflag] = useState(null)

  const handleOptionSelect = (id) => {
    setSelectedOption(id)
    setSelectedShippingMethod(id)
  }

  useEffect(() => {
    if (addressInfo.coords) {
      if (pinflagPoints.length > 0) {
        const closestPinflag = findClosestPoint(pinflagPoints, addressInfo.coords)
        setClosestPinflag(closestPinflag)
      }

      if (localPoints.length > 0) {
        const closestLocal = findClosestPoint(localPoints, addressInfo.coords)
        setClosestLocal(closestLocal)
      }
    } else {
      const pinflagPoint = findPointByDistrictOrRegion(
        pinflagPoints,
        addressInfo.district,
        addressInfo.region
      )

      const localPoint = findPointByDistrictOrRegion(
        localPoints,
        addressInfo.district,
        addressInfo.region
      )

      if (pinflagPoint) {
        setClosestPinflag({ point: pinflagPoint, distance: null })
      }

      if (localPoint) {
        setClosestLocal({ point: localPoint, distance: null })
      }
    }
  }, [localPoints, pinflagPoints])

  const handleReturnButton = () => {
    const updatedSteps = [...steps]
    updatedSteps[0].completed = true
    updatedSteps[1].disabled = false
    updatedSteps[1].subSteps[0].completed = false
    updatedSteps[1].subSteps[1].completed = false
    setSteps(updatedSteps)
  }

  const handleNextButton = () => {
    setPointsData((prevState) => {
      prevState.localPointSelected = closestLocal?.point
      prevState.pinflagPointSelected = closestPinflag?.point
      return prevState
    })

    if (selectedOption && selectedOption.includes('-delivery')) {
      const splittedType = selectedOption.split('-delivery')[0]
      const foundService = deliveryInfo.services?.find(
        (service) => service.serviceType === splittedType
      )

      if (foundService) {
        setAddressInfo((prevState) => ({
          ...prevState,
          serviceType: splittedType,
          serviceName: foundService.name || foundService.description,
          price: foundService.price,
          courier: foundService.courier
        }))
        setServiceAndShippingcost(foundService, setSelectedServiceType, setCartData)
      }
    }

    const updatedSteps = [...steps]
    updatedSteps[0].completed = true
    updatedSteps[1].disabled = false
    updatedSteps[1].subSteps[0].completed = true
    updatedSteps[1].subSteps[1].completed = true
    setSteps(updatedSteps)
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <motion.div
      className={`md:h-full bg-slate-100 grow flex flex-col py-2 md:w-[517px] ${ecommerceInfo.cms === 'woocommerce' && 'md:bg-white'}`}
      layout
    >
      <motion.div
        className="bg-white rounded-lg mx-[21px] px-3 md:px-0 mb-2"
        layout
      >
        <SectionTitle
          className='px-5 py-3'
          title={`${addressInfo.userAddress}, ${addressInfo.district}`}
          subtitle={`${addressInfo.aditionalInfo || ''}`}
          iconName='home'
          iconCustomStyle={{ fill: 'white', stroke: 'white', strokeWidth: 0.5 }}
          primaryColor={primaryColor}
          showButton={true}
          buttonLabel='Cambiar'
          onButtonClick={handleReturnButton}
          buttonDisabled={false}
          buttonLoading={false}
          buttonLeftIcon={<Icon name='change' className='mr-1' width={12} />}
          buttonFontSize='0.7rem'
        />
      </motion.div>

      <div>
        <p className="text-center text-gray-800 text-sm font-sm">
          Elige cómo quieres recibir tu compra
        </p>
      </div>
      <div className='overflow-y-auto max-h-[calc(100vh-260px)] flex flex-col gap-y-2 py-2 scrollbar' >
        {localPoints.length > 0 && (
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.4, delay: 0.1, ease: 'easeInOut' }}
          >
            <ServiceSection
              sectionTitle="Retira en tienda"
              iconName="store"
              options={[
                {
                  id: 'local-pickup',
                  label: closestLocal?.point.properties.deliveryTime || 'N/A',
                  loading: !closestLocal?.point.properties.title,
                  badgeIcon: addressInfo?.isManualAddress ? undefined : 'person',
                  badgeText: addressInfo?.isManualAddress
                    ? undefined
                    : `${closestLocal?.distance?.toFixed(2)} km`,
                  rightText: addressInfo.isManualAddress
                    ? undefined
                    : closestLocal?.point.properties.price === 0
                      ? 'Gratis'
                      : `$${numberParser(closestLocal?.point.properties.price)}`
                }
              ]}
              selectedOption={selectedOption}
              handleOptionSelect={handleOptionSelect}
              color={secondaryColor}
              cms={ecommerceInfo.cms}
            />
          </motion.div>
        )}
        {pinflagPoints.length > 0 && (
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.4, delay: 0.2, ease: 'easeInOut' }}
          >
            <ServiceSection
              sectionTitle="Retira en un punto"
              iconName="point"
              iconWidth={22}
              options={[
                {
                  id: 'click-and-collect',
                  label: closestPinflag?.point.properties.deliveryTime || 'N/A',
                  loading: !closestPinflag?.point.properties.title,
                  badgeIcon: addressInfo?.isManualAddress ? undefined : 'person',
                  badgeText: addressInfo?.isManualAddress
                    ? undefined
                    : `${closestPinflag?.distance?.toFixed(2)} km`,
                  rightText: addressInfo.isManualAddress
                    ? undefined
                    : closestPinflag?.point.properties.price === 0
                      ? 'Gratis'
                      : `$${numberParser(closestPinflag?.point.properties.price)}`
                }
              ]}
              selectedOption={selectedOption}
              handleOptionSelect={handleOptionSelect}
              color={secondaryColor}
              cms={ecommerceInfo.cms}
            />
          </motion.div>
        )}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.4, delay: 0.3, ease: 'easeInOut' }}
        >
          <ServiceSection
            sectionTitle="Despacho a domicilio"
            iconName="delivery"
            options={deliveryInfo?.services?.map((service) => ({
              id: `${service.serviceType}-delivery`,
              label: service.description?.trim() ? service.description : service.name,
              rightText: `$${numberParser(service.price)}`,
              preText: service.serviceType === 'express' ? 'Express' : undefined,
              preTextIcon: service.serviceType === 'express' ? 'flash' : undefined,
              preTextClassName: service.serviceType === 'express' ? 'text-green-600 font-semibold' : undefined,
              preTextIconClassName: service.serviceType === 'express' ? 'fill-green-600 w-4 h-4' : undefined
            }))}
            selectedOption={selectedOption}
            handleOptionSelect={handleOptionSelect}
            color={secondaryColor}
            cms={ecommerceInfo.cms}
          />
        </motion.div>
      </div>
      <div className='flex flex-col mt-6 md:mt-1 mx-[21px] bg-white md:bg-transparent rounded px-0 md:px-4'>
        <div className='w-full text-center px-[21px] md:px-0'>
          <StepNavigationButtons
            labelOne="Atrás"
            onClickOne={handleReturnButton}
            iconOneName="arrow-back"
            classNameOne="bg-white center text-black"
            labelTwo={
              selectedOption
                ? selectedOption.includes('-delivery')
                  ? 'Continuar'
                  : selectedOption === 'click-and-collect'
                    ? 'Elegir punto'
                    : 'Elegir tienda'
                : 'Elegir tienda'
            }
            isDisabledTwo={!selectedOption}
            onClickTwo={handleNextButton}
            iconTwoName={
              selectedOption && selectedOption.includes('-delivery')
                ? ''
                : 'go'
            }
            classNameTwo="bg-black text-white"
            buttonTwoColor={secondaryColor}
            iconTwoWidth={21}
          />
        </div>
      </div>

    </motion.div>
  )
}

export default ShippingMethodStep
